import type { ThreadPlacement } from "@afar-research/contracts";

export const emptyThreadPlacement: ThreadPlacement = {
  pinnedAt: null,
  pinOrderKey: null,
  activeOrderKey: null,
};

/**
 * Fractional index keys over a fixed digit alphabet. Threads sort by plain
 * string comparison of these keys, so moving one thread writes ONE key to
 * ONE thread and never touches its neighbours — two clients reordering at
 * the same time converge instead of fighting over a shared index.
 */
const PLACEMENT_DIGITS = "abcdefghijklmnopqrstuvwxyz";

function isValidPlacementKey(key: string): boolean {
  if (key.length === 0) return false;
  for (const char of key) {
    if (!PLACEMENT_DIGITS.includes(char)) return false;
  }
  // A trailing minimum digit would leave no room to sort a key immediately
  // before this one; generators never produce it, so treat it as corrupt.
  return key.at(-1) !== PLACEMENT_DIGITS[0];
}

/**
 * Midpoint of two digit strings read as fractions in (0, 1). `""` stands for
 * the open bound on either side, and `a` must sort before `b`.
 */
function placementMidpoint(a: string, b: string): string {
  if (b !== "" && a >= b) throw new Error("placementMidpoint: bounds out of order");
  if (b !== "") {
    // Recurse past the longest common prefix; the shorter side pads with the
    // minimum digit.
    let shared = 0;
    while ((a.charAt(shared) || PLACEMENT_DIGITS[0]) === b.charAt(shared)) shared += 1;
    if (shared > 0) return b.slice(0, shared) + placementMidpoint(a.slice(shared), b.slice(shared));
  }
  const digitA = a === "" ? 0 : PLACEMENT_DIGITS.indexOf(a.charAt(0));
  const digitB = b === "" ? PLACEMENT_DIGITS.length : PLACEMENT_DIGITS.indexOf(b.charAt(0));
  if (digitB - digitA > 1) return PLACEMENT_DIGITS.charAt(Math.round((digitA + digitB) / 2));
  // Consecutive leading digits: either b has spare digits to shorten into, or
  // extend a. The base case midpoint("", "") is the middle of the alphabet, so
  // this never produces a trailing minimum digit.
  if (b.length > 1) return b.charAt(0);
  return PLACEMENT_DIGITS.charAt(digitA) + placementMidpoint(a.slice(1), "");
}

/**
 * A key sorting strictly between two neighbours. Null bounds mean the top of
 * the block or the bottom of its arranged run. Returns null rather than
 * throwing when the existing keys are corrupt or out of order, so callers can
 * fall back to rewriting the whole block.
 */
export function placementKeyBetween(before: string | null, after: string | null): string | null {
  const a = before ?? "";
  const b = after ?? "";
  if (a !== "" && !isValidPlacementKey(a)) return null;
  if (b !== "" && !isValidPlacementKey(b)) return null;
  if (b !== "" && a >= b) return null;
  return placementMidpoint(a, b);
}

/**
 * Evenly spaced keys for materializing a whole order at once. Wider keys keep
 * a long list from exhausting the space between two-digit keys.
 */
export function generateSpreadPlacementKeys(count: number): string[] {
  let width = 2;
  let space = PLACEMENT_DIGITS.length ** width;
  while (space <= (count + 1) * 2) {
    width += 1;
    space *= PLACEMENT_DIGITS.length;
  }
  const step = space / (count + 1);
  const keys: string[] = [];
  for (let index = 0; index < count; index += 1) {
    let value = Math.round(step * (index + 1));
    // Skip values whose low digit is the minimum, which would be a trailing
    // "a" key that nothing can sort before.
    if (value % PLACEMENT_DIGITS.length === 0) value += 1;
    let key = "";
    for (let digit = 0; digit < width; digit += 1) {
      key = PLACEMENT_DIGITS.charAt(value % PLACEMENT_DIGITS.length) + key;
      value = Math.floor(value / PLACEMENT_DIGITS.length);
    }
    keys.push(key);
  }
  return keys;
}

export interface PlacementAssignment {
  readonly id: string;
  readonly orderKey: string;
}

/**
 * The writes that realize a new order. When the moved thread lands between
 * two keyed (or absent) neighbours this is a single write to the moved
 * thread. When a neighbour is keyless — every thread starts that way — the
 * block is materialized with fresh spread keys once, and every move after
 * that is single-write again. Both the pinned block and the Active list use
 * this planner; only the key they write differs.
 */
export function planPlacementReorder(input: {
  /** Thread ids in the desired visual order, after the move. */
  readonly orderedIds: readonly string[];
  /** Keys for every thread, including rows hidden from the drag; only `orderedIds` get writes. */
  readonly keysById: ReadonlyMap<string, string | null | undefined>;
  readonly movedId: string;
}): readonly PlacementAssignment[] {
  const { orderedIds, keysById, movedId } = input;
  const visibleIds = new Set(orderedIds);
  const reservedKeys = new Set(
    [...keysById].flatMap(([id, key]) => (!visibleIds.has(id) && key != null ? [key] : [])),
  );
  const movedIndex = orderedIds.indexOf(movedId);
  if (movedIndex === -1) return [];
  const beforeId = movedIndex > 0 ? (orderedIds[movedIndex - 1] ?? null) : null;
  const afterId = movedIndex < orderedIds.length - 1 ? (orderedIds[movedIndex + 1] ?? null) : null;
  const beforeKey = beforeId !== null ? (keysById.get(beforeId) ?? null) : null;
  const afterKey = afterId !== null ? (keysById.get(afterId) ?? null) : null;
  if ((beforeId === null || beforeKey !== null) && (afterId === null || afterKey !== null)) {
    let key = placementKeyBetween(beforeKey, afterKey);
    while (key !== null && reservedKeys.has(key)) key = placementKeyBetween(key, afterKey);
    if (key !== null) return [{ id: movedId, orderKey: key }];
  }
  const keys = generateSpreadPlacementKeys(orderedIds.length + reservedKeys.size)
    .filter((key) => !reservedKeys.has(key))
    .slice(0, orderedIds.length);
  return orderedIds.flatMap((id, index) => {
    const key = keys[index];
    if (key === undefined || keysById.get(id) === key) return [];
    return [{ id, orderKey: key }];
  });
}

function sortableTimestamp(iso: string | null | undefined): number {
  if (!iso) return 0;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Identity tiebreak for equal keys. Thread ids are only unique within an
 * AFAR Environment and the sidebar merges environments, so both parts are
 * needed or two clients could render equal-key threads in arrival order.
 */
function identityTiebreak(
  left: { readonly id: string; readonly environmentId?: string | undefined },
  right: { readonly id: string; readonly environmentId?: string | undefined },
): number {
  return (
    left.id.localeCompare(right.id) ||
    (left.environmentId ?? "").localeCompare(right.environmentId ?? "")
  );
}

/**
 * What ordering needs from a thread. Deliberately structural rather than a
 * `Pick` of the shell: both sorts are plain string and instant comparisons,
 * and the sidebar merges threads from several AFAR Environments, so the
 * identity tiebreak takes the environment alongside the id.
 */
export interface PlacedThread {
  readonly id: string;
  readonly createdAt: string;
  readonly placement?: ThreadPlacement | null | undefined;
  readonly unsettledAt?: string | null | undefined;
  readonly environmentId?: string | undefined;
}

/**
 * Pinned block order: user-arranged keys first, then keyless threads newest
 * first — so a thread pinned before it was ever dragged joins the top of the
 * unarranged run rather than breaking the arranged one.
 */
export function sortPinnedThreads<T extends PlacedThread>(threads: readonly T[]): T[] {
  if (threads.length < 2) return [...threads];
  const keyed: T[] = [];
  const keyless: T[] = [];
  for (const thread of threads) {
    (thread.placement?.pinOrderKey != null ? keyed : keyless).push(thread);
  }
  keyed.sort((left, right) => {
    const leftKey = left.placement?.pinOrderKey ?? "";
    const rightKey = right.placement?.pinOrderKey ?? "";
    return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : identityTiebreak(left, right);
  });
  keyless.sort(
    (left, right) =>
      sortableTimestamp(right.createdAt) - sortableTimestamp(left.createdAt) ||
      identityTiebreak(left, right),
  );
  return [...keyed, ...keyless];
}

/**
 * The Active list's sort anchor: creation time, re-anchored to `unsettledAt`
 * when the thread last re-entered the list. The list stays still between
 * lifecycle transitions, but a thread that wakes surfaces at the top instead
 * of sinking back to its creation slot.
 */
function activeAnchorMs(thread: PlacedThread): number {
  return Math.max(sortableTimestamp(thread.createdAt), sortableTimestamp(thread.unsettledAt));
}

/**
 * Active list order: unarranged threads lead, newest anchor first, then the
 * threads the user has dragged into a fixed order. Activity moves nothing —
 * a running turn must not make a row jump under the pointer.
 */
export function sortActiveThreads<T extends PlacedThread>(threads: readonly T[]): T[] {
  if (threads.length < 2) return [...threads];
  const anchors = new Map<T, number>();
  for (const thread of threads) {
    if (thread.placement?.activeOrderKey == null) anchors.set(thread, activeAnchorMs(thread));
  }
  return [...threads].sort((left, right) => {
    const leftKey = left.placement?.activeOrderKey ?? null;
    const rightKey = right.placement?.activeOrderKey ?? null;
    if (leftKey === null && rightKey !== null) return -1;
    if (leftKey !== null && rightKey === null) return 1;
    const order =
      leftKey !== null && rightKey !== null
        ? leftKey < rightKey
          ? -1
          : leftKey > rightKey
            ? 1
            : 0
        : (anchors.get(right) ?? 0) - (anchors.get(left) ?? 0);
    return order || identityTiebreak(left, right);
  });
}

/** Pinning an already pinned thread keeps its slot: the existing key wins. */
export function pinThreadPlacement(
  current: ThreadPlacement | null | undefined,
  pinnedAt: string,
  orderKey: string | undefined,
): ThreadPlacement {
  return {
    activeOrderKey: current?.activeOrderKey ?? null,
    pinnedAt: current?.pinnedAt ?? pinnedAt,
    pinOrderKey: current?.pinnedAt != null ? (current.pinOrderKey ?? null) : (orderKey ?? null),
  };
}

/** Unpinning drops the pinned slot and returns the thread to the Active list. */
export function unpinThreadPlacement(current: ThreadPlacement | null | undefined): ThreadPlacement {
  return {
    ...emptyThreadPlacement,
    activeOrderKey: current?.activeOrderKey ?? null,
  };
}

export function reorderPinnedThreadPlacement(
  current: ThreadPlacement | null | undefined,
  pinnedAt: string,
  orderKey: string,
): ThreadPlacement {
  return {
    activeOrderKey: current?.activeOrderKey ?? null,
    pinnedAt: current?.pinnedAt ?? pinnedAt,
    pinOrderKey: orderKey,
  };
}

export function reorderActiveThreadPlacement(
  current: ThreadPlacement | null | undefined,
  orderKey: string,
): ThreadPlacement {
  return {
    pinnedAt: current?.pinnedAt ?? null,
    pinOrderKey: current?.pinOrderKey ?? null,
    activeOrderKey: orderKey,
  };
}

/** A pinned thread outranks its settled or snoozed stamp in the sidebar. */
export function isThreadPinned(thread: {
  readonly placement?: ThreadPlacement | null | undefined;
}): boolean {
  return thread.placement?.pinnedAt != null;
}
